<template>
  <div>
    <SoPageHead :title="$t('securone.nav.users')">
      <template #subtitle>{{ $tc("securone.users.count", users.length, { n: users.length }) }} · {{ $tc("securone.common.sms_contacts_count", smsContacts.length, { n: smsContacts.length }) }} · {{ $tc("securone.users.pending_count", pendingCount, { n: pendingCount }) }}</template>
      <select v-if="showDepartmentFilter" v-model="departmentId" class="sd-select sd-select--sm">
        <option value="">{{ $t("securone.department.filter_all") }}</option>
        <option v-for="d in departments" :key="d._id" :value="d._id">{{ d.name }}</option>
      </select>
      <SoSearch v-model="search" :placeholder="$t('securone.users.search')" />
      <div class="flex items-center gap-2">
        <button v-if="canImport" class="sd-btn sd-btn--secondary" :title="$t('securone.import.intro')" @click="$refs.importUsers.open()"><BaseIcon icon="file-import" class="mr-2" />{{ $t("securone.import.button") }}</button>
        <button v-if="canWrite.invite" class="sd-btn sd-btn--success" @click="$refs.invite.open('basic')">{{ $t("securone.users.invite") }}<BaseIcon icon="plus" class="ml-2" /></button>
      </div>
    </SoPageHead>

    <SoNote v-if="!canWrite.invite" tone="info" class="mb-4">{{ $t("securone.users.readonly_note") }}</SoNote>

    <div class="sd-card overflow-hidden mb-6">
      <div class="overflow-x-auto">
        <table class="sd-table sd-table--compact w-full text-sm">
          <thead>
            <tr>
              <th v-if="canWrite.members" class="w-8"><input type="checkbox" class="form-checkbox h-4 w-4" :checked="allSelected" @change="toggleAll" /></th>
              <th class="text-left">{{ $t("securone.users.user") }}</th>
              <th v-if="showDepartmentColumn" class="text-left">{{ $t("securone.department.column") }}</th>
              <th class="text-left">{{ $t("securone.users.level") }}</th>
              <th class="text-left">{{ $t("securone.invite.email") }}</th>
              <th class="text-left">{{ $t("securone.common.groups") }}</th>
              <th class="text-left">{{ $t("securone.users.last_online") }}</th>
              <th v-if="anyEditable"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in visibleUsers" :key="u._id" class="border-b border-secondary-100 last:border-0" :class="{ 'bg-secondary-50': selected.includes(u._id) }">
              <td v-if="canWrite.members" class="px-4 py-3"><input type="checkbox" class="form-checkbox h-4 w-4" :value="u._id" v-model="selected" /></td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <SoAvatar :name="u.name" size="sm" :muted="u.pending" />
                  <div>
                    <div class="font-semibold" :class="u.pending ? 'text-gray-500' : 'text-gray-800'">{{ u.name }}<span v-if="u.isMe" class="text-gray-400 font-normal"> ({{ $t("securone.common.you") }})</span></div>
                    <div v-if="u.pending" class="text-xxs text-gray-400">{{ $t("securone.users.invitation_sent", { date: dateStamp(u.invitedAt) }) }}</div>
                  </div>
                </div>
              </td>
              <td v-if="showDepartmentColumn" class="px-4 py-3 text-xs text-gray-600">{{ u.level === "customer" ? $t("securone.scope.customer") : u.departmentNames.join(", ") || "—" }}</td>
              <td class="px-4 py-3"><SoChip :tone="u.level === 'customer' ? 'role' : 'default'">{{ $t("securone.level." + u.level) }}</SoChip></td>
              <td class="px-4 py-3 text-xs text-gray-600">{{ u.email }}</td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1">
                  <SoChip v-if="u.pending">{{ $t("securone.users.invited_pending") }}</SoChip>
                  <SoChip v-for="g in u.groups" :key="g._id">{{ g.name }}</SoChip>
                  <span v-if="!u.groups.length && !u.pending" class="text-xs text-gray-400">{{ $t("securone.users.no_groups") }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-xs" :class="u.lastLogin ? 'text-gray-600' : 'text-gray-400'">{{ u.lastLogin ? dateTime(u.lastLogin) : $t("securone.users.never") }}</td>
              <td v-if="anyEditable" class="px-4 py-3 text-right whitespace-nowrap">
                <button v-if="u.canEdit" class="sd-btn sd-btn--secondary sd-btn--sm" @click="$refs.editUser.open(u)">{{ $t("edit") }}<BaseIcon icon="pencil-alt" class="ml-1" /></button>
                <button v-if="u.canRemove" class="sd-btn sd-btn--secondary sd-btn--sm so-btn-icon ml-1 text-red-600" :title="$t('securone.users.remove_user')" @click="confirmRemoveUser(u)"><BaseIcon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!filteredUsers.length"><td colspan="8" class="px-4 py-8 text-center text-gray-400">{{ $t("securone.users.none") }}</td></tr>
            <!-- long lists: the first 10, then "show all" -->
            <tr v-if="filteredUsers.length > PAGE">
              <td colspan="8" class="px-4 py-2 text-center bg-secondary-50">
                <button type="button" class="text-sm text-securdia-blue hover:underline" @click="showAll = !showAll">
                  <BaseIcon :icon="showAll ? 'chevron-up' : 'chevron-down'" class="mr-1" />{{ showAll ? $t("securone.users.show_fewer") : $t("securone.users.show_all", { n: filteredUsers.length - PAGE }) }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex items-baseline gap-3 mb-2">
      <h2 class="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{{ $t("securone.common.sms_contacts") }}</h2>
      <div class="text-xs text-gray-500">{{ $t("securone.users.sms_help") }}</div>
      <button v-if="canWrite.invite" class="sd-btn sd-btn--secondary sd-btn--sm ml-auto" @click="$refs.invite.open('sms')">{{ $t("securone.users.add_sms_contact") }}<BaseIcon icon="plus" class="ml-1" /></button>
    </div>
    <div class="sd-card overflow-hidden mb-4">
      <div v-for="s in filteredSms" :key="s._id" class="flex items-center gap-3 px-4 py-2 border-b border-secondary-100 last:border-0 text-sm">
        <SoAvatar :name="s.name" size="sm" muted />
        <div class="w-56 text-gray-800">{{ s.name }}</div>
        <div class="w-40 text-xs text-gray-600 font-mono">{{ s.phone }}</div>
        <div v-if="showDepartmentColumn" class="w-48 text-xs text-gray-600">{{ s.departmentNames.join(", ") }}</div>
        <div class="flex-1 flex flex-wrap gap-1">
          <SoChip v-for="g in s.groups" :key="g._id">{{ g.name }}</SoChip>
          <span v-if="!s.groups.length" class="text-xs text-gray-400">{{ $t("securone.users.sms_no_groups") }}</span>
        </div>
        <div class="text-xs text-gray-400 hidden md:block">{{ $t("securone.users.never_signs_in") }}</div>
        <button v-if="s.canEdit" class="sd-btn sd-btn--secondary sd-btn--sm" @click="$refs.editSms.open(s)">{{ $t("edit") }}<BaseIcon icon="pencil-alt" class="ml-1" /></button>
      </div>
      <div v-if="!filteredSms.length" class="px-4 py-6 text-center text-gray-400 text-sm">{{ $t("securone.users.no_sms_contacts") }}</div>
    </div>

    <SoNote tone="muted">{{ $t("securone.users.membership_note") }}</SoNote>

    <!-- floating action bar for bulk group assignment (groups this actor may edit) -->
    <div v-if="canWrite.members && selected.length" class="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40 flex items-center gap-3 bg-white border border-secondary-300 rounded shadow-xl px-4 py-3">
      <span class="text-sm text-gray-700">{{ $tc("securone.users.selected", selected.length, { n: selected.length }) }}</span>
      <select v-model="bulkGroup" class="sd-select sd-select--sm">
        <option value="">{{ $t("securone.users.choose_group") }}</option>
        <option v-for="g in editableGroups" :key="g._id" :value="g._id">{{ g.name }}</option>
      </select>
      <button class="sd-btn sd-btn--primary" :disabled="!bulkGroup || bulkBusy" @click="addToGroup">{{ $t("securone.users.add_to_group") }}</button>
      <button v-if="canWrite.invite && resendableSelected.length" class="sd-btn sd-btn--secondary" :disabled="bulkBusy" :title="$t('securone.users.resend_selected_help')" @click="confirmResendSelected"><BaseIcon icon="envelope" class="mr-1" />{{ $t("securone.users.resend_selected", { n: resendableSelected.length }) }}</button>
      <button v-if="removableSelected.length" class="sd-btn sd-btn--danger" :disabled="bulkBusy" :title="$t('securone.users.remove_selected_help')" @click="confirmRemoveSelected"><BaseIcon icon="trash" class="mr-1" />{{ $t("securone.users.remove_selected", { n: removableSelected.length }) }}</button>
      <button class="sd-btn sd-btn--secondary" @click="selected = []">{{ $t("securone.common.cancel") }}</button>
    </div>

    <InviteUserModal ref="invite" :groups="editableGroups" @invited="onInvited" />
    <ImportUsersModal ref="importUsers" :groups="editableGroups" @imported="onImported" />
    <EditUserModal ref="editUser" @saved="onUserSaved" @remove="confirmRemoveUser" />
    <EditSmsContactModal ref="editSms" :groups="editableGroups" @saved="load" @remove="confirmRemoveSms" />
  </div>
</template>

<script>
import SoPageHead from "./components/SoPageHead.vue";
import SoSearch from "./components/SoSearch.vue";
import SoChip from "./components/SoChip.vue";
import SoNote from "./components/SoNote.vue";
import SoAvatar from "./components/SoAvatar.vue";
import InviteUserModal from "./components/InviteUserModal.vue";
import ImportUsersModal from "./components/ImportUsersModal.vue";
import EditUserModal from "./components/EditUserModal.vue";
import EditSmsContactModal from "./components/EditSmsContactModal.vue";
import api from "./securone.api";
import { dateStamp, dateTime, errorMessage, escapeHtml } from "./securone.helpers";

// D-30 / D-31. Department is a filter, not only a displayed value. Three
// levels (customer, department, basic) plus SMS contacts. Only basic users and
// SMS contacts are invited here (V10); customer and department users are
// created in SecurCloud.
export default {
  name: "SecurOneUsers",
  components: { SoPageHead, SoSearch, SoChip, SoNote, SoAvatar, InviteUserModal, ImportUsersModal, EditUserModal, EditSmsContactModal },
  data() {
    // PAGE: rows shown before "show all"
    return { PAGE: 10, showAll: false, users: [], smsContacts: [], groups: [], search: "", selected: [], bulkGroup: "", bulkBusy: false, departmentId: this.$store.getters["securone/departmentFilter"] || "" };
  },
  computed: {
    canWrite() {
      return this.$store.getters["securone/canWrite"];
    },
    // bulk import: Securdia admins only (the server enforces the same)
    canImport() {
      return this.canWrite.invite && this.$store.getters["securone/kind"] === "securdia_admin";
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    showDepartmentFilter() {
      return this.$store.getters["securone/showDepartmentFilter"];
    },
    showDepartmentColumn() {
      return this.$store.getters["securone/showDepartmentColumn"];
    },
    adminVersion() {
      return this.$store.state.securone.adminVersion;
    },
    editableGroups() {
      return this.groups.filter((g) => g.canEdit);
    },
    anyEditable() {
      return this.users.some((u) => u.canEdit);
    },
    pendingCount() {
      return this.users.filter((u) => u.pending).length;
    },
    filteredUsers() {
      const q = this.search.trim().toLowerCase();
      return q ? this.users.filter((u) => [u.name, u.email, u.phone, ...u.departmentNames, ...u.groups.map((g) => g.name)].join(" ").toLowerCase().includes(q)) : this.users;
    },
    visibleUsers() {
      return this.showAll || this.filteredUsers.length <= this.PAGE ? this.filteredUsers : this.filteredUsers.slice(0, this.PAGE);
    },
    filteredSms() {
      const q = this.search.trim().toLowerCase();
      return q ? this.smsContacts.filter((s) => [s.name, s.phone, ...s.departmentNames].join(" ").toLowerCase().includes(q)) : this.smsContacts;
    },
    // only basic users this actor may remove; customer and department users are never removed here
    removableSelected() {
      return this.users.filter((u) => this.selected.includes(u._id) && u.canRemove);
    },
    // basic users whose welcome mail this actor may send again (customer and department users get theirs from SecurCloud)
    resendableSelected() {
      return this.users.filter((u) => this.selected.includes(u._id) && u.canResendInvite);
    },
    allSelected() {
      return this.filteredUsers.length > 0 && this.filteredUsers.every((u) => this.selected.includes(u._id));
    },
  },
  methods: {
    dateStamp,
    dateTime,
    async load() {
      try {
        const [people, groups] = await Promise.all([api.users({ departmentId: this.departmentId || undefined }), api.groups()]);
        this.users = people.users;
        this.smsContacts = people.smsContacts;
        this.groups = groups;
        this.selected = this.selected.filter((id) => this.users.some((u) => u._id === id));
      } catch (error) {
        this.handleError(error);
      }
    },
    toggleAll() {
      this.selected = this.allSelected ? [] : this.filteredUsers.map((u) => u._id);
    },
    async addToGroup() {
      const group = this.groups.find((g) => g._id === this.bulkGroup);
      if (!group) return;
      this.bulkBusy = true;
      try {
        const members = Array.from(new Set([...(group.members || []).map(String), ...this.selected]));
        await api.updateGroup(group._id, { members }); // V2 decides on the server; an ineligible pick is refused, not narrowed
        this.$toasted.show(this.$t("securone.users.added_to_group_toast", { n: this.selected.length, group: group.name }), { type: "success", theme: "outline", position: "top-right", duration: 5000 });
        this.selected = [];
        this.bulkGroup = "";
        await this.load();
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      } finally {
        this.bulkBusy = false;
      }
    },
    onImported(result) {
      this.$toasted.show(this.$tc("securone.import.created_count", result.created.length, { n: result.created.length }), { type: result.created.length ? "success" : "info", theme: "outline", position: "top-right", duration: 6000 });
      this.load();
    },
    onInvited(result) {
      const text = result.kind === "basic" ? this.$t("securone.invite.sent_toast", { email: result.user.email, name: result.user.name }) : this.$t("securone.invite.contact_added_toast", { name: result.contact.name });
      this.$toasted.show(text, { type: "success", theme: "outline", position: "top-right", duration: 7000 });
      this.load();
    },
    onUserSaved(result) {
      if (result && result.dropped && result.dropped.droppedGroups && result.dropped.droppedGroups.length) {
        this.$toasted.show(this.$t("securone.group_editor.dropped_toast", { n: result.dropped.droppedGroups.length }), { type: "info", theme: "outline", position: "top-right", duration: 6000 });
      }
      this.load();
    },
    // a confirmation; red for removals, blue (safe = true) for a harmless action such as sending a mail again
    dialog(title, text, verb, onConfirm, safe = false) {
      this.$modal.show("dialog", {
        title: safe ? title : `<span class="text-red-500">${title}</span>`,
        text: `<div class="text-sm text-gray-700 text-left">${text}</div>`,
        buttons: [
          { title: `<div class="${safe ? "bg-gray-400" : "bg-success-500"} text-white text-sm font-sans py-2">${this.$t("securone.common.cancel")}</div>`, handler: () => this.$modal.hide("dialog") },
          { title: `<div class="${safe ? "bg-primary-500" : "bg-red-500"} text-white text-sm font-sans py-2">${verb}</div>`, handler: () => { this.$modal.hide("dialog"); onConfirm(); } },
        ],
      });
    },
    confirmResendSelected() {
      const list = this.resendableSelected;
      if (!list.length) return;
      const skipped = this.selected.length - list.length;
      const names = list.slice(0, 8).map((u) => escapeHtml(u.name)).join(", ") + (list.length > 8 ? " …" : "");
      const body = this.$t("securone.users.resend_selected_consequence", { names }) + (skipped ? "<br><br>" + this.$tc("securone.users.resend_selected_skipped", skipped, { n: skipped }) : "");
      this.dialog(this.$t("securone.users.resend_selected_title", { n: list.length }), body, this.$t("securone.users.resend"), async () => {
        this.bulkBusy = true;
        let sent = 0;
        let failed = 0;
        for (const u of list) {
          try {
            await api.resendInvite(u._id);
            sent += 1;
          } catch (error) {
            failed += 1;
          }
        }
        this.bulkBusy = false;
        this.selected = [];
        this.$toasted.show(this.$t("securone.users.resent_many_toast", { n: sent }) + (failed ? " " + this.$tc("securone.users.resent_many_failed", failed, { n: failed }) : ""), { type: failed ? "error" : "success", theme: "outline", position: "top-right", duration: 6000 });
      }, true);
    },
    confirmRemoveSelected() {
      const list = this.removableSelected;
      if (!list.length) return;
      const skipped = this.selected.length - list.length;
      const names = list.slice(0, 8).map((u) => escapeHtml(u.name)).join(", ") + (list.length > 8 ? " …" : "");
      const body = this.$t("securone.users.remove_selected_consequence", { names }) + (skipped ? "<br><br>" + this.$tc("securone.users.remove_selected_skipped", skipped, { n: skipped }) : "");
      this.dialog(this.$t("securone.users.remove_selected_title", { n: list.length }), body, this.$t("securone.users.remove"), async () => {
        this.bulkBusy = true;
        let removed = 0;
        let failed = 0;
        for (const u of list) {
          try {
            await api.deleteUser(u._id);
            removed += 1;
          } catch (error) {
            failed += 1;
          }
        }
        this.bulkBusy = false;
        this.selected = [];
        this.$toasted.show(this.$t("securone.users.removed_many_toast", { n: removed }) + (failed ? " " + this.$tc("securone.users.removed_many_failed", failed, { n: failed }) : ""), { type: failed ? "error" : "success", theme: "outline", position: "top-right", duration: 6000 });
        this.load();
      });
    },
    confirmRemoveUser(user) {
      this.$modal.hide("securone-edit-user");
      const groups = user.groups.map((g) => g.name).join(", ");
      this.dialog(this.$t("securone.users.remove_title", { name: escapeHtml(user.name) }), this.$t("securone.users.remove_consequence", { groups: escapeHtml(groups) || this.$t("securone.users.no_groups") }), this.$t("securone.users.remove"), async () => {
        try {
          await api.deleteUser(user._id);
          this.$toasted.show(this.$t("securone.users.removed_toast", { name: user.name }), { type: "success", theme: "outline", position: "top-right", duration: 5000 });
          this.load();
        } catch (error) {
          this.handleError(error, errorMessage(this, error));
        }
      });
    },
    confirmRemoveSms(contact) {
      this.$modal.hide("securone-edit-sms");
      const groups = contact.groups.map((g) => g.name).join(", ");
      this.dialog(this.$t("securone.users.remove_title", { name: escapeHtml(contact.name) }), this.$t("securone.users.remove_sms_consequence", { groups: escapeHtml(groups) || this.$t("securone.users.no_groups") }), this.$t("securone.users.remove"), async () => {
        try {
          await api.deleteSmsContact(contact._id);
          this.$toasted.show(this.$t("securone.users.removed_toast", { name: contact.name }), { type: "success", theme: "outline", position: "top-right", duration: 5000 });
          this.load();
        } catch (error) {
          this.handleError(error, errorMessage(this, error));
        }
      });
    },
  },
  watch: {
    adminVersion() {
      this.load();
    },
    departmentId(value) {
      this.$store.commit("securone/SET_DEPARTMENT_FILTER", value || null);
      this.load();
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.users"), "securone_users");
    this.load();
  },
};
</script>
